import React, { forwardRef } from 'react';
import { tr } from '../../i18n/i18n';
import { ModalHandle, PrimaryModal } from './';
import { AppButton } from '../atoms';

interface DirtyLeaveConfirmModalProps {
  title?: string;
  message?: string;
  stayLabel?: string;
  leaveLabel?: string;
  icon?: string; // Optional icon class (e.g., 'pi-exclamation-triangle')
  width?: string; // Optional width class (e.g., 'w-[28rem]')
  severity?: 'primary' | 'warning' | 'danger' | 'info' | 'success'; // Severity level for styling
  onStay: () => void;
  onLeave: () => void;
}

const DirtyLeaveConfirmModal = forwardRef<ModalHandle, DirtyLeaveConfirmModalProps>(
  (
    {
      title = tr('g.confirm_leave', 'Confirm Leave'),
      message = tr('g.you_have_unsaved_changes_are_you_sure_you_want_to_leave_2', tr('g.unsaved_changes_leave', 'You have unsaved changes. Are you sure you want to leave?')),
      stayLabel = tr('g.no_stay', 'No, Stay'),
      leaveLabel = tr('g.yes_leave', 'Yes, Leave'),
      icon = 'pi-exclamation-triangle', // Default to warning icon
      width = '400px', // Default width (20rem)
      severity = 'danger', // Default to primary
      onStay,
      onLeave,
    },
    ref,
  ) => {
    // Severity-based styling
    const severityStyles = {
      primary: {
        bg: 'bg-purple-50',
        border: 'border-purple-300',
        icon: 'text-project-purple',
      },
      warning: {
        bg: 'bg-yellow-50',
        border: 'border-yellow-300',
        icon: 'text-yellow-600',
      },
      danger: {
        bg: 'bg-red-50',
        border: 'border-red-300',
        icon: 'text-red-600',
      },
      info: {
        bg: 'bg-blue-50',
        border: 'border-blue-300',
        icon: 'text-blue-600',
      },
      success: {
        bg: 'bg-green-50',
        border: 'border-green-300',
        icon: 'text-green-600',
      },
    };

    const styles = severityStyles[severity];

    return (
      <PrimaryModal
        ref={ref}
        width={width}
        showHeader={false}
        content={
          <div className="flex flex-col items-center justify-center gap-4 p-4">
            {/* Icon with Severity-Based Styling - Eye Catching */}
            <div
              className={`p-3 rounded-full flex items-center justify-center shadow-lg border-2 aspect-square ${styles.bg} ${styles.border}`}
            >
              <i className={`pi ${icon} text-3xl ${styles.icon}`}></i>
            </div>

            {/* Title moved inside content (below icon) */}
            <h3 className="text-md font-medium text-center font-poppins">{title}</h3>

            {/* Message */}
            <div className="text-sm font-small text-center font-poppins">{message}</div>

            {/* Action Buttons */}
            <div className="flex gap-2 mt-2 w-full justify-end">
              <AppButton label={stayLabel} outlined onClick={onStay} />
              <AppButton label={leaveLabel} onClick={onLeave} severity="danger" />
            </div>
          </div>
        }
      />
    );
  },
);

DirtyLeaveConfirmModal.displayName = 'DirtyLeaveConfirmModal';

export default DirtyLeaveConfirmModal;
